import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useChatStore } from "../../../stores/ChatStore";
import moment from "moment-timezone";
import { KTSVG } from "../../../_metronic/helpers";
import CheckboxList from "./CheckboxList";
import { ChatMessageBubble } from "./ChatMessageBubble";
import { ChatMessage } from "../../../interfaces/Chat/Chat";
import { useSettingsStore } from "../../../stores/SettingsStore";
import { formatSecondsToTime, getDateRangeDuration } from "../../../functions/general";
import { displayDate } from "../../../functions/chat";
import { useIntl } from "react-intl";

interface Props {
    handleIntSync: (isFirst: boolean, isNew: boolean, type: 'chat', dataRoom: ChatMessage) => void;
}

const ChatMessageList: FC<Props> = ({ handleIntSync }) => {
    const {
        messages,
        phoneNumber,
        mode,
        showChat,
        scrollToId,
        setScrollToId,
        setIsSyncRoom,
        isOpenCheckBoxForward,
        listChatAgents
    } = useChatStore();
    const { settings } = useSettingsStore()
    const intl = useIntl()
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [dataLastChat, setDataLastChat] = useState<ChatMessage>({} as ChatMessage);
    const [topData, setTopData] = useState<ChatMessage>({} as ChatMessage);
    const parentRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: messages.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 400,
        overscan: 20,
    });

    const items = virtualizer.getVirtualItems();
    const dataAllSessions = [...(listChatAgents[phoneNumber] ?? [])]
    const dataEndSessions = [...(listChatAgents[phoneNumber] ?? [])].filter((s) => s.ended?.date)

    const scrollToChat = (index: number) => {
        if (!virtualizer.scrollElement) return;
        virtualizer.scrollToIndex(index, { align: "center" });

        requestAnimationFrame(() => {
            const lastMessageElement = document.getElementById(`message-${index}`);
            if (lastMessageElement) {
                lastMessageElement.scrollIntoView({ behavior: "smooth", block: "center" });
            } else {
                virtualizer.scrollElement!.scrollTo({
                    top: virtualizer.scrollElement!.scrollHeight,
                    behavior: "smooth",
                });
            }
        });
    };

    const handleScrollToButtonClick = () => {
        scrollToChat(messages.length - 1);
        setIsAtBottom(true);
    };

    const handleSyncMessage = (data: ChatMessage) => {
        let resultData = {} as ChatMessage
        if (data) {
            resultData = data
        } else {
            let newData = {
                id: "",
                message: "",
                receivedAt: moment().format("YYYY-MM-DD HH:mm:ss"),
                attachmentName: null,
                updatedAt: null,
                fromMe: false,
                idReference: null,
                isForwarded: false,
                isJMessageAvailable: false,
                sender: null,
                phone: phoneNumber.split('-')[0],
                as: phoneNumber.split('-')[1],
                action: {
                    isEditing: false
                }
            }

            resultData = newData
        }

        setIsSyncRoom(() => true)
        setDataLastChat(resultData)
        handleIntSync(false, false, 'chat', resultData as ChatMessage);
    };

    useEffect(() => {
        if (showChat && mode === "normal") {
            scrollToChat(messages.length - 1);
        } else if (showChat && scrollToId && mode === "search") {
            scrollToChat(messages.findIndex((message) => message.id === scrollToId));
            setScrollToId("");
        } else if (showChat && mode === "sync-room") {
            scrollToChat(messages.findIndex((message) => message.id === dataLastChat.id));
            setScrollToId("");
        }
    }, [phoneNumber, scrollToId, mode, messages, showChat]);

    useEffect(() => {
        const handleScroll = () => {
            const lastIndex = messages.length - 1;
            const lastItem = items.find((item) => item.index === lastIndex);

            const scrollTop = virtualizer.scrollElement?.scrollTop || 0;
            const scrollBottom =
                scrollTop + (virtualizer.scrollElement?.clientHeight || 0);

            const isLastMessageVisible =
                lastItem &&
                lastItem.start >= scrollTop &&
                lastItem.end <= scrollBottom;

            setIsAtBottom(messages.length === 0 ? true : !!isLastMessageVisible);
        };

        const scrollElement = parentRef.current;
        scrollElement?.addEventListener("scroll", handleScroll);
        return () => scrollElement?.removeEventListener("scroll", handleScroll);
    }, [items, messages.length, virtualizer]);

    useEffect(() => {
        const topVisibleIndex = virtualizer.getVirtualItems()?.[0]?.index;
        setTopData(() => topVisibleIndex == 0 ? {} as ChatMessage : messages[topVisibleIndex + 19]);
    }, [virtualizer.getVirtualItems(), messages]);

    const { startSessionMap, endSessionMap } = useMemo(() => {
        const startSessionMap: Record<string, typeof dataAllSessions[number][]> = {};
        const endSessionMap: Record<string, typeof dataEndSessions[number][]> = {};

        if (!messages.length || (!dataAllSessions.length && !dataEndSessions.length)) {
            return { startSessionMap, endSessionMap };
        }

        const sortedStartSessions = dataAllSessions
        const sortedEndSessions = dataEndSessions

        let startPointer = 0;
        let endPointer = 0;

        for (let i = 0; i < messages.length; i++) {
            const currentTime = moment(messages[i].receivedAt, "YYYY-MM-DD HH:mm:ss");
            const nextTime = i + 1 < messages.length
                ? moment(messages[i + 1].receivedAt, "YYYY-MM-DD HH:mm:ss")
                : null;

            // Start Session Matching
            while (
                startPointer < sortedStartSessions.length &&
                moment(sortedStartSessions[startPointer].started!.date).isSameOrBefore(currentTime)
            ) {
                const session = sortedStartSessions[startPointer];
                const msgId = messages[i].id;
                if (!startSessionMap[msgId]) startSessionMap[msgId] = [];
                startSessionMap[msgId].push(session);
                startPointer++;
            }

            // End Session Matching
            while (
                endPointer < sortedEndSessions.length &&
                moment(sortedEndSessions[endPointer].ended!.date).isAfter(currentTime) &&
                (nextTime === null || moment(sortedEndSessions[endPointer].ended!.date).isSameOrBefore(nextTime))
            ) {
                const session = sortedEndSessions[endPointer];
                const msgId = messages[i].id;
                if (!endSessionMap[msgId]) endSessionMap[msgId] = [];
                endSessionMap[msgId].push(session);
                endPointer++;
            }
        }

        return { startSessionMap, endSessionMap };
    }, [messages, dataAllSessions, dataEndSessions]);

    return (
        <div
            ref={parentRef}
            className="card-body my-2"
            style={{
                height: "100%",
                overflowY: "auto",
                position: "relative",
                contain: "strict",
            }}
        >
            {
                topData && Object.keys(topData).length > 0 && (
                    <span
                        style={{
                            position: "sticky",
                            top: "0px",
                            right: "0px",
                            zIndex: 9999,
                            width: "fit-content",
                            display: "block",
                            left: "50%",
                            transform: "translateX(-50%)",
                        }}
                    >
                        <p className="badge badge-light-primary fs-7 fw-bold text-center">
                            {displayDate(topData, intl)}
                        </p>
                    </span>
                )
            }

            <div
                style={{
                    height: virtualizer.getTotalSize(),
                    width: "100%",
                    position: "relative",
                }}
            >

                <div
                    style={{
                        // position: "absolute",
                        // top: 0,
                        // left: 0,
                        width: "100%",
                        transform: `translateY(${items[0]?.start ?? 0}px)`,
                    }}
                >
                    <div className="d-flex justify-content-center">
                        <button
                            style={{ borderRadius: "50%" }}
                            type="button"
                            className="btn btn-light btn-sm btn-icon mb-3"
                            onClick={() => handleSyncMessage(messages[0])}
                        >
                            <i className="fas fa-redo-alt fs-4 menu-icon" />
                        </button>
                    </div>

                    {items.map((virtualItem) => {
                        const index = virtualItem.index;
                        const message = messages[index];
                        const prevMessage = messages[index - 1];

                        const showDate =
                            index === 0 ||
                            moment(message.receivedAt).format("DD MMMM YYYY") !==
                            moment(prevMessage?.receivedAt).format("DD MMMM YYYY");

                        const showStartSession = startSessionMap[message.id];
                        const showEndSession = endSessionMap[message.id];

                        return (
                            <div
                                key={index}
                                id={`message-${index}`}
                                data-index={index}
                                ref={virtualizer.measureElement}
                                className={`message-item ${isOpenCheckBoxForward ? "message-with-checkbox" : ""}`}
                            >
                                {showDate && (
                                    <div className="d-flex justify-content-center">
                                        <span className="badge badge-light-primary fs-7 fw-bold mb-2">
                                            {displayDate(message, intl)}
                                        </span>
                                    </div>
                                )}

                                {settings.chat_version === "v2" && showStartSession?.length > 0 && showStartSession.map((s, i) => (
                                    <div key={`${index}-start-session-${i}`} className="separator separator-content border-dark py-15" >
                                        <span className="fs-7 fw-bolder" style={{width: "1000px"}}>
                                            {intl.formatMessage({ id: "FORM.LABEL.SESSION_START" })}<br />
                                            <i className="fs-7">{intl.formatMessage({ id: "FORM.LABEL.AGENT" })} : {s.started.name}</i> <br />
                                            <i className="fs-7">{intl.formatMessage({ id: "FORM.LABEL.DATE" }, { title: '' })} : {moment(s.started.date).utc(true).format("DD MMMM YYYY HH:mm")}</i>
                                        </span>
                                    </div>
                                ))}

                                <div className="position-relative">
                                    {isOpenCheckBoxForward && <CheckboxList message={message} />}
                                    <ChatMessageBubble
                                        message={message}
                                        key={message.id}
                                        scrollToChat={scrollToChat}
                                        isAction={true}
                                    />
                                </div>

                                {settings.chat_version === "v2" && showEndSession?.length > 0 && showEndSession.map((s, i) => (
                                    <div key={`${index}-end-session-${i}`} className="separator separator-content border-dark py-15" >
                                        <span className="fs-7 fw-bolder"  style={{width: "1000px"}}>
                                            {intl.formatMessage({ id: "FORM.LABEL.SESSION_END" })}<br />
                                            <i className="fs-7">{intl.formatMessage({ id: "FORM.LABEL.AGENT" })} : {s.ended.name}</i> <br />
                                            <i className="fs-7">{intl.formatMessage({ id: "FORM.LABEL.DATE" }, { title: '' })} : {moment(s.ended.date).utc(true).format("DD MMMM YYYY HH:mm")}</i><br />
                                            <i className="fs-7">{intl.formatMessage({ id: "FORM.LABEL.DURATION" })} : {getDateRangeDuration(s.started.date, s.ended.date)}</i><br />
                                            <i className="fs-7">{intl.formatMessage({ id: "FORM.LABEL.AVERAGE_RESPONSE_TIME" })} : {formatSecondsToTime(s.avgReplySpeedInSeconds)}</i>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>

            {
                !isAtBottom && (
                    <span
                        style={{
                            position: "sticky",
                            bottom: 0,
                            right: 0,
                        }}
                    >
                        <p className="text-end">
                            <span
                                style={{
                                    cursor: "pointer",
                                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                                }}
                                className="bg-white border border-1 py-6 px-4 rounded"
                                onClick={handleScrollToButtonClick}
                            >
                                <KTSVG
                                    path="media/icons/duotune/arrows/arr004.svg"
                                    className="svg-icon-dark svg-icon-2x"
                                />
                            </span>
                        </p>
                    </span>
                )
            }
        </div >
    );
};

export { ChatMessageList };
